// ManDana — Tabs: one responsive home for navigation and universal search.
// ("Tabs" is the internal name; users see this surface as "More".)
const { mdIsLight: mdIsLightF, mdRgba: mdRgbaF } = window;
const { useState: useStateF, useRef: useRefF, useEffect: useEffectF,
  useLayoutEffect: useLayoutEffectF, useMemo: useMemoF } = React;
const MD_FIND_KEY = /Mac|iPhone|iPad/.test((navigator.platform || '')) ? '⌘K' : 'Ctrl+K';
const fixedIcon = (size) => ({ width: size, height: size, minWidth: size, minHeight: size,
  flex: `0 0 ${size}px` });

function FindMark({ color, size = 19 }) {
  return <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24"
    style={fixedIcon(size)}>
    <circle cx="10.8" cy="10.8" r="6.8" fill="none" stroke={color} strokeWidth="1.8" />
    <path d="M15.8 15.8l4.4 4.4" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
  </svg>;
}
function FindGlyph({ go, category, color }) {
  const s = { fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const box = { width: 18, height: 18, viewBox: '0 0 24 24', style: fixedIcon(18) };
  if (category === 'Record') return <svg aria-hidden="true" {...box} data-md-glyph="record"><path d="M6 3.5h8l4 4v13H6z" {...s}/><path d="M14 3.5v4h4M9 12h6M9 16h6" {...s}/></svg>;
  if (category === 'View') return <svg aria-hidden="true" {...box}><path d="M12 4L3.8 8.2 12 12.4l8.2-4.2zM5.8 12l6.2 3.2 6.2-3.2M5.8 15.8L12 19l6.2-3.2" {...s}/></svg>;
  if (go === 'crafts' || category === 'Craft') return <svg aria-hidden="true" {...box} data-md-glyph="craft">
    <path d="M12 3H6a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3v-6" {...s}/>
    <path d="M19 2c0 2 1.2 3.2 3.2 3.2-2 0-3.2 1.2-3.2 3.2 0-2-1.2-3.2-3.2-3.2 2 0 3.2-1.2 3.2-3.2z" fill={color} stroke="none"/>
  </svg>;
  if (go === 'day') return <svg aria-hidden="true" {...box}><rect x="4" y="5.5" width="16" height="14.5" rx="3.5" {...s}/><path d="M4 10h16M8.5 3.5v3M15.5 3.5v3" {...s}/></svg>;
  if (go === 'board' || go === 'boards') return window.MDI.desk(color, 18);
  if (go === 'database' || go === 'databases') return <svg aria-hidden="true" {...box}><rect x="4" y="4.5" width="16" height="15" rx="3" {...s}/><path d="M4 10h16M4 15h16M10 10v9.5" {...s}/></svg>;
  if (go === 'settings') return <svg aria-hidden="true" {...box} data-md-glyph="setting"><circle cx="12" cy="12" r="3" {...s}/><path d="M12 3v3m0 12v3M3 12h3m12 0h3M5.6 5.6l2.1 2.1m8.6 8.6 2.1 2.1M18.4 5.6l-2.1 2.1m-8.6 8.6-2.1 2.1" {...s}/></svg>;
  return <svg aria-hidden="true" {...box}><path d="M12 3.5l7.5 4.3v8.4L12 20.5l-7.5-4.3V7.8L12 3.5z" {...s}/><path d="M12 12v8.5M12 12L4.7 7.9M12 12l7.3-4.1" {...s}/></svg>;
}

const FIXED = [
  { target: 'window:projects', title: 'Projects', place: { go: 'workshop' }, category: 'Project' },
  { target: 'window:databases', title: 'Databases', place: { go: 'databases' }, category: 'Database' },
  { target: 'window:crafts', title: 'Crafts', place: { go: 'crafts' }, category: 'Craft' },
  { target: 'window:boards', title: 'Desks', place: { go: 'boards' }, category: 'Desk' },
  { target: 'window:settings', title: 'Settings', place: { go: 'settings' }, category: 'Setting' },
];
const KIND_OPTIONS = ['Record', 'Project', 'View', 'Desk', 'Craft', 'Pursuit', 'Part of Life', 'Database'];
function categoryFor(place, key) {
  if (place.go === 'settings') return 'Setting';
  if (place.go === 'view' || String(key || '').includes(':view:')) return 'View';
  if (place.go === 'board' || place.go === 'boards') return 'Desk';
  if (place.go === 'crafts') return 'Craft';
  if (place.go === 'record' || (place.go === 'database' && place.row)) return 'Record';
  if (place.go === 'project' || place.go === 'workshop') return 'Project';
  if (place.go === 'database' || place.go === 'databases') return 'Database';
  if (place.go === 'pursuit') return 'Pursuit';
  if (place.go === 'domain') return 'Part of Life';
  return 'Record';
}
function cleanPreview(value, title = '') {
  const lines = String(value || '').replace(/[\uE000\uE001]/g, '').split('\n');
  const cleanTitle = String(title || '').replace(/^[#>\-*\s]+/, '').trim();
  if (lines.length && lines[0].replace(/^[#>\-*\s]+/, '').trim() === cleanTitle) lines.shift();
  return lines.join('\n').trim();
}
function partTitle(id) {
  const row = (window.MD_DOMAINS || []).find((item) => item.id === id);
  return row?.label || row?.name || '';
}
function placeForResolved(found, key, override = null) {
  if (override) return override;
  const id = found?.ref?.id;
  if (found?.type === 'project') return { go: 'project', id };
  if (found?.type === 'pursuit') return { go: 'pursuit', id };
  if (found?.type === 'domain') return { go: 'domain', id };
  if (found?.type === 'craft' || found?.type === 'manifest') return { go: 'crafts', id };
  if (found?.type === 'board') return { go: 'board', id };
  if (found?.type === 'database') return { go: 'database', id };
  const parsed = window.mdRecordRefParse?.(key) || found?.ref;
  const stable = parsed && window.mdRecordRef?.(parsed.type, parsed.id);
  const ref = window.mdRecordRefKey?.(stable) || key;
  return { go: 'record', ref };
}
function rowFromResolved(found, key, recordCtx, override = null, propertyText = '') {
  if (!found) return null;
  const place = placeForResolved(found, key, override);
  const target = window.mdNavigationTarget?.(place);
  if (!target) return null;
  const project = found.projectId ? window.mdProjectById?.(found.projectId) : null;
  const databaseId = found.databaseId || (place.go === 'database' ? place.id : null);
  const database = databaseId ? window.mdDatabaseRead?.(databaseId) : null;
  const part = partTitle(found.domainId);
  const snippet = cleanPreview(found.text, found.title);
  return { target, title: found.title || 'Untitled', snippet, searchText: [found.title, found.text, propertyText].join('\n'),
    place, category: categoryFor(place, key), writtenAt: Number(found.at) || 0,
    part, project: project?.name || (found.type === 'project' ? found.title : ''),
    database: database?.name || (found.type === 'database' ? found.title : ''), recordCtx };
}
function savedViewRows() {
  const out = [];
  const add = (title, place, context) => {
    const target = window.mdNavigationTarget(place);
    if (target) out.push({ target, title: title || 'Untitled view', place, category: 'View',
      snippet: context || '', searchText: `${title || ''}\n${context || ''}`, writtenAt: 0 });
  };
  const projectDefaults = new Set(['recboard', 'reclist', 'rectime']);
  for (const project of (window.mdProjectIndex?.({ open: false }) || [])) {
    for (const view of (window.mdWorklistViews?.(project.id) || []))
      if (!projectDefaults.has(view.id)) add(view.name,
        { go: 'view', scope: 'project', projectId: project.id, id: view.id }, `Project · ${project.name}`);
  }
  for (const database of (window.mdDatabaseList?.() || [])) {
    for (const view of (database.views || []).slice(1))
      add(view.name, { go: 'view', scope: 'database', databaseId: database.id, id: view.id }, `Database · ${database.name}`);
  }
  return out;
}
function buildCatalog(recordCtx) {
  const out = [], seen = new Set();
  const add = (row) => { if (row && !seen.has(row.target)) { seen.add(row.target); out.push(row); } };
  for (const candidate of (window.mdRecordCandidates?.('', recordCtx) || [])) {
    const found = window.mdRecordResolve?.(candidate.key, recordCtx);
    if (!found) continue;
    if (!found.title && !String(found.text || '').trim()) continue;
    add(rowFromResolved(found, candidate.key, recordCtx));
  }
  for (const database of (window.mdDatabaseList?.() || [])) {
    for (const record of (database.rows || [])) {
      const key = `dbrow:${record.id}`;
      const found = window.mdRecordResolve?.(key, recordCtx) || { ref: { type: 'dbrow', id: record.id },
        type: 'dbrow', title: record.values?.title || 'New Record', text: record.body || '', databaseId: database.id,
        domainId: null, at: record.updated || record.created || 0 };
      const properties = Object.values(record.values || {}).map((value) => typeof value === 'string' ? value : JSON.stringify(value)).join('\n');
      add(rowFromResolved(found, key, recordCtx, null, properties));
    }
  }
  for (const view of savedViewRows()) add(view);
  return out;
}
function titleFromTarget(target, recordCtx) {
  const fixed = FIXED.find((x) => x.target === target);
  if (fixed) return fixed;
  const place = window.mdNavigationPlace(target);
  if (!place) return null;
  if (place.go === 'settings' && place.view) {
    /* The names Find announces a settings screen by. Keyed on the RESOLVED
       view, so `setting:city` and `setting:place` are the same row under the
       same name rather than two entries for one screen. */
    const titles = { workflows: 'Workflows', method: 'Calculation Method',
      place: 'Your Location', trash: 'Trash', data: 'Your Data',
      profile: 'Your Profile', devices: 'Passkeys & Devices' };
    const title = titles[place.view];
    return title ? { target, title, snippet: 'Settings', searchText: title,
      place, category: 'Setting', writtenAt: 0 } : null;
  }
  if (place.go === 'day') return null;
  if (place.go === 'record') {
    const found = window.mdRecordResolve?.(place.ref, recordCtx);
    return rowFromResolved(found, place.ref, recordCtx);
  }
  let refKey = '';
  if (place.go === 'project') refKey = `project:${place.id}`;
  if (place.go === 'pursuit') refKey = `pursuit:${place.id}`;
  if (place.go === 'domain') refKey = `domain:${place.id}`;
  if (place.go === 'board') refKey = `board:${place.id}`;
  if (place.go === 'crafts' && place.id) refKey = `craft:${place.id}`;
  if (place.go === 'database' && !place.row) refKey = `database:${place.id}`;
  if (place.go === 'database' && place.row) refKey = `dbrow:${place.row}`;
  if (refKey) {
    const found = window.mdRecordResolve?.(refKey, recordCtx);
    const row = rowFromResolved(found, refKey, recordCtx, place);
    if (row) return row;
  }
  let title = '', context = '';
  if (place.go === 'view' && place.scope === 'project') {
    title = window.mdWorklistViews?.(place.projectId)?.find((view) => view.id === place.id)?.name || '';
    context = `Project · ${window.mdProjectById?.(place.projectId)?.name || 'Untitled project'}`;
  }
  if (place.go === 'view' && place.scope === 'database') {
    const database = window.mdDatabaseRead?.(place.databaseId);
    title = database?.views?.find((view) => view.id === place.id)?.name || '';
    context = `Database · ${database?.name || 'Untitled database'}`;
  }
  if (!title) return null;
  return { target, title, snippet: context, searchText: `${title}\n${context}`, place, category: 'View', writtenAt: 0 };
}

function writeNavigationDrag(event, row) {
  const transfer = event?.dataTransfer;
  const type = window.MD_NAVIGATION_DRAG_TYPE;
  if (!transfer || !type || !row?.target) return;
  const href = window.mdNavigationHref?.(row.target) || '';
  transfer.effectAllowed = 'copy';
  transfer.setData(type, row.target);
  /* Keep the ordinary share address too. A drop in another app is still a
     link; only a ManDana Desk understands that it already owns the target. */
  if (href) {
    transfer.setData('text/uri-list', href);
    transfer.setData('text/plain', href);
  }
}

function LinearRow({ row, active, onOpen, favorite, onFavorite, mobile, accent }) {
  const light = mdIsLightF();
  const touch = useRefF(null);
  const [hot, setHot] = useStateF(false);
  return <div data-md-linear-row="true" data-md-kind={row.category} style={{ position: 'relative', display: 'grid',
    gridTemplateColumns: '88px minmax(0,1fr) 38px', alignItems: 'center', minHeight: 52,
    borderRadius: 12, background: active ? mdRgbaF(accent, light ? .1 : .15) : 'transparent' }}
    onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
    onTouchEnd={(e) => { if (touch.current != null && Math.abs(e.changedTouches[0].clientX - touch.current) > 48) onFavorite(); touch.current = null; }}
    onMouseEnter={() => setHot(true)} onMouseLeave={() => setHot(false)}
    onFocusCapture={() => setHot(true)} onBlurCapture={() => setHot(false)}>
    <span style={{ paddingInlineStart: 10, color: ink(.38), fontSize: 11.5, fontWeight: 610,
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.category}</span>
    <a href={window.mdNavigationHref?.(row.target) || '#'} draggable="true"
      onDragStart={(event) => writeNavigationDrag(event, row)}
      onClick={(event) => { event.preventDefault(); onOpen(); }} style={{ minWidth: 0, border: 0, background: 'transparent', color: ink(.9),
      display: 'flex', alignItems: 'center', gap: 10, textAlign: 'start', textDecoration: 'none',
      padding: '9px 6px', cursor: 'pointer' }}>
      <FindGlyph go={row.place.go} category={row.category} color={active ? mdRgbaF(accent, 1) : ink(.52)} />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14.5, fontWeight: 570, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title || 'Untitled'}</span>
        {row.snippet && <span style={{ display: 'block', color: ink(.4), fontSize: 12, marginTop: 2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(row.snippet).replace(/[\uE000\uE001]/g, '')}</span>}
      </span>
    </a>
    <button aria-label={favorite ? `Remove ${row.title} from Favorites` : `Add ${row.title} to Favorites`}
      title={favorite ? 'Unfavorite' : 'Favorite'} onClick={onFavorite}
      style={{ width: 34, height: 34, border: 0, borderRadius: 9, background: 'transparent', cursor: 'pointer',
        color: favorite ? mdRgbaF(accent, 1) : ink(.36), fontSize: 20,
        opacity: mobile || favorite || hot ? 1 : 0, transition: 'opacity .12s ease' }}>
      {favorite ? '★' : '☆'}
    </button>
  </div>;
}

function FavoriteMoveMark({ color = 'currentColor' }) {
  return <svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 18 18"
    style={fixedIcon(18)} fill={color}>
    {[5, 9, 13].flatMap((y) => [6, 12].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.15" />))}
  </svg>;
}

function FavoriteCard({ row, accent, onOpen, onFavorite, onMoveStart, onMoveKey,
  moving = false, landing = null }) {
  const recordLike = row.category === 'Record';
  const projectLike = row.category === 'Project';
  const previewKind = recordLike ? 'record' : projectLike ? 'project' : 'destination';
  const meta = [row.project && `Project · ${row.project}`, row.database && `Database · ${row.database}`,
    row.part && `Part of Life · ${row.part}`].filter(Boolean).join('  ·  ');
  /* The card is the DAY cards' material (polish 8): the same Glass primitive
     at EventCard's resting values — radius 20, surf(0.07), stroke 0.18 — so
     the two card families read as one and a glass tune reaches both. The
     shape is the panel's width with height following at 1:1.618, the golden
     rectangle lying down; the article owns geometry and drag identity, the
     Glass owns every visual. */
  return <article data-md-favorite-preview={previewKind} data-md-drag-id={row.target}
    data-md-favorite-moving={moving || undefined} data-md-drop-target={landing || undefined}
    style={{ position: 'relative', width: '100%', aspectRatio: '1.618 / 1', boxSizing: 'border-box',
      display: moving ? 'none' : 'block' }}>
    <Glass radius={20} tint={surf(0.07)} stroke={0.18} style={{ height: '100%', boxSizing: 'border-box' }}>
    <a href={window.mdNavigationHref?.(row.target) || '#'} draggable="true"
      onDragStart={(event) => writeNavigationDrag(event, row)}
      onClick={(event) => { event.preventDefault(); onOpen(); }}
      aria-label={`Open ${row.title}`} style={{ display: 'block', height: '100%', boxSizing: 'border-box',
        paddingBlock: 16, paddingInlineStart: 17, paddingInlineEnd: 88,
        color: ink(.9), textDecoration: 'none', cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: ink(.42), fontSize: 11.5,
        fontWeight: 650, letterSpacing: .3, textTransform: 'uppercase' }}>
        <FindGlyph go={row.place.go} category={row.category} color={mdRgbaF(accent, .82)} />{row.category}
      </div>
      <h3 dir="auto" style={{ margin: '13px 0 8px', fontSize: projectLike ? 20 : 18, lineHeight: 1.2,
        fontWeight: 670, letterSpacing: -.2 }}>{row.title || 'Untitled'}</h3>
      <div dir="auto" style={{ minHeight: 38, color: ink(.52), fontSize: 13.2, lineHeight: 1.52,
        whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {row.snippet || (recordLike ? 'This record has no writing beneath its title yet.' : 'Open this destination to see its full page.')}
      </div>
      {meta && <footer style={{ marginTop: 12, color: ink(.33), fontSize: 10.8,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta}</footer>}
    </a>
    <div style={{ position: 'absolute', insetBlockStart: 11, insetInlineEnd: 11,
      display: 'flex', alignItems: 'center', gap: 3 }}>
      <button type="button" aria-label={`Move ${row.title} within Favorites`} title="Drag to reorder"
        onPointerDown={onMoveStart} onKeyDown={onMoveKey}
        style={{ width: 34, height: 34, border: 0, borderRadius: 10, padding: 0,
          display: 'grid', placeItems: 'center', background: 'transparent', color: ink(.38),
          cursor: moving ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' }}>
        <FavoriteMoveMark />
      </button>
      <button aria-label={`Remove ${row.title} from Favorites`} title="Unfavorite" onClick={onFavorite}
        style={{ width: 34, height: 34, border: 0, borderRadius: 10, background: 'transparent',
          color: mdRgbaF(accent, 1), fontSize: 20, cursor: 'pointer' }}>★</button>
    </div>
    </Glass>
    {/* The reorder landing signal, over the glass rather than baked into it:
        an accent hairline round the card and a 2px line at the edge the drop
        would take. */}
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, borderRadius: 20,
      pointerEvents: 'none', border: `1px solid ${mdRgbaF(accent, .34)}`,
      boxShadow: landing ? `inset 0 ${landing === 'after' ? '-2px' : '2px'} 0 ${mdRgbaF(accent, .95)}` : 'none',
      opacity: landing ? 1 : 0, transition: 'opacity .14s ease' }} />
  </article>;
}

const FAVORITE_SLIDE_MS = 160;
const FAVORITE_SETTLE_MS = 140;
const FAVORITE_EASE = 'cubic-bezier(.22,.61,.36,1)';

function favoriteReorder(ids, moved, target, edge) {
  if (!ids.includes(moved) || !target || moved === target) return null;
  const out = ids.filter((id) => id !== moved);
  const at = out.indexOf(target);
  if (at < 0) return null;
  out.splice(at + (edge === 'after' ? 1 : 0), 0, moved);
  return out.every((id, index) => id === ids[index]) ? null : out;
}

function favoriteSeats(nodes) {
  const out = {};
  for (const node of nodes) {
    const box = node.getBoundingClientRect?.();
    if (box?.height) out[node.getAttribute('data-md-drag-id')] = box.top;
  }
  return out;
}

/* A one-column version of the Projects Board hand: the card leaves a live
   seat, surrounding cards FLIP into their new places, the held copy follows
   the pointer, and the nearest scrolling ancestor moves while the hand leans
   into either vertical edge. The explicit handle means touch needs no hold. */
function FavoriteStack({ rows, accent, onOpen, onFavorite }) {
  const zone = useRefF(null), seats = useRefF(null), gesture = useRefF(null);
  const ghost = useRefF(null), dragRef = useRefF(null), scrollFrame = useRefF(0);
  const [drag, setDragState] = useStateF(null);
  const setDrag = (value) => { dragRef.current = typeof value === 'function' ? value(dragRef.current) : value;
    setDragState(dragRef.current); };
  const ids = rows.map((row) => row.target);
  const flow = drag ? rows.filter((row) => row.target !== drag.id) : rows;
  const overAt = drag?.over ? flow.findIndex((row) => row.target === drag.over) : -1;
  const gapAt = drag ? (overAt < 0 ? flow.length : overAt + (drag.edge === 'after' ? 1 : 0)) : -1;

  const placeGhost = (x, y) => {
    const held = gesture.current, node = ghost.current;
    if (!held || !node) return;
    node.style.transform = `translate3d(${x - held.base.left - held.grabX}px, ${y - held.base.top - held.grabY}px, 0)`;
  };

  useLayoutEffectF(() => {
    const held = gesture.current, node = ghost.current;
    if (!drag || !held || !node || node.firstChild) return;
    const copy = held.source.cloneNode(true);
    const strip = (item) => {
      for (const name of item.getAttributeNames?.() || []) if (name.startsWith('data-md-')) item.removeAttribute(name);
      if (/^(BUTTON|A|INPUT|SELECT|TEXTAREA)$/.test(item.tagName || '')) item.setAttribute('tabindex', '-1');
      for (const child of item.children || []) strip(child);
    };
    strip(copy); copy.style.display = 'block'; copy.style.width = '100%';
    node.style.width = `${held.width}px`; node.style.borderRadius = held.radius;
    node.append(copy); placeGhost(held.lastX, held.lastY);
  }, [drag?.id]);

  /* The same FLIP arithmetic as the Projects Board: measure where cards look,
     remove the previous transforms, measure their real seats, invert, release. */
  useLayoutEffectF(() => {
    const host = zone.current;
    if (!host) return undefined;
    const nodes = [...host.querySelectorAll('[data-md-drag-id]')];
    if (!drag) {
      seats.current = null;
      for (const node of nodes) { node.style.transition = ''; node.style.transform = ''; }
      return undefined;
    }
    const shown = favoriteSeats(nodes);
    for (const node of nodes) { node.style.transition = 'none'; node.style.transform = ''; }
    const now = favoriteSeats(nodes), was = seats.current;
    seats.current = now;
    if (!was || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const playing = [];
    for (const node of nodes) {
      const id = node.getAttribute('data-md-drag-id');
      if (!(id in was) || !(id in now)) continue;
      const flight = id in shown ? shown[id] - now[id] : 0;
      const dy = was[id] + flight - now[id];
      if (Math.abs(dy) < .5) continue;
      node.style.transform = `translateY(${dy}px)`; playing.push(node);
    }
    if (!playing.length) return undefined;
    host.getBoundingClientRect();
    const frame = window.requestAnimationFrame(() => {
      for (const node of playing) { node.style.transition = `transform ${FAVORITE_SLIDE_MS}ms ${FAVORITE_EASE}`; node.style.transform = ''; }
    });
    return () => window.cancelAnimationFrame?.(frame);
  }, [drag?.id, drag?.over, drag?.edge, gapAt]);

  useEffectF(() => () => { if (scrollFrame.current) window.cancelAnimationFrame?.(scrollFrame.current); }, []);

  const saveOrder = (next) => { if (next?.length) window.mdNavigationSaveOrder?.(next); };
  const moveKey = (row, index, event) => {
    let to = null;
    if (event.key === 'ArrowUp') to = Math.max(0, index - 1);
    if (event.key === 'ArrowDown') to = Math.min(rows.length - 1, index + 1);
    if (event.key === 'Home') to = 0;
    if (event.key === 'End') to = rows.length - 1;
    if (to === null || to === index) return;
    event.preventDefault();
    const next = [...ids], [held] = next.splice(index, 1); next.splice(to, 0, held); saveOrder(next);
  };

  const begin = (event, row) => {
    if (rows.length < 2 || (event.button != null && event.button !== 0)) return;
    event.preventDefault(); event.stopPropagation();
    const source = event.currentTarget.closest('[data-md-drag-id]');
    const box = source?.getBoundingClientRect?.();
    const page = document.documentElement.hasAttribute('data-md-phone-tabs-page');
    const baseNode = source?.closest('[data-md-tabs-surface]');
    const base = page ? { left: 0, top: 0 } : (baseNode?.getBoundingClientRect?.() || { left: 0, top: 0 });
    const scroller = page ? window : source?.closest('.md-scroll');
    const held = { id: row.target, source, base, scroller, page, x: event.clientX || 0, y: event.clientY || 0,
      lastX: event.clientX || 0, lastY: event.clientY || 0, live: false,
      grabX: box ? (event.clientX || 0) - box.left : 0, grabY: box ? (event.clientY || 0) - box.top : 0,
      width: box?.width || 0, height: box?.height || 159,
      radius: source && window.getComputedStyle ? window.getComputedStyle(source).borderRadius : '18px', lean: 0 };
    gesture.current = held;

    const nearest = (y) => {
      let best = null;
      for (const node of zone.current?.querySelectorAll('[data-md-drag-id]') || []) {
        const id = node.getAttribute('data-md-drag-id'); if (id === held.id) continue;
        const at = node.getBoundingClientRect?.(); if (!at?.height) continue;
        const away = y < at.top ? at.top - y : y > at.bottom ? y - at.bottom : 0;
        if (!best || away < best.away) best = { id, away, edge: y > at.top + at.height / 2 ? 'after' : 'before' };
      }
      return best || { id: null, edge: null };
    };
    const aim = () => {
      const at = nearest(held.lastY);
      setDrag((old) => old && old.id === held.id && old.over === at.id && old.edge === at.edge
        ? old : { id: held.id, over: at.id, edge: at.edge, height: held.height, settling: false });
    };
    const stopScroll = () => { held.lean = 0;
      if (scrollFrame.current) window.cancelAnimationFrame?.(scrollFrame.current); scrollFrame.current = 0; };
    const scrollStep = () => {
      scrollFrame.current = 0;
      if (gesture.current !== held || !held.live || !held.lean || !held.scroller) return;
      if (held.page) window.scrollBy(0, held.lean);
      else held.scroller.scrollTop += held.lean;
      aim(); placeGhost(held.lastX, held.lastY);
      scrollFrame.current = window.requestAnimationFrame(scrollStep);
    };
    const lean = () => {
      const vv = window.visualViewport;
      const at = held.page
        ? { top: vv?.offsetTop || 0, bottom: (vv?.offsetTop || 0) + (vv?.height || window.innerHeight), height: vv?.height || window.innerHeight }
        : held.scroller?.getBoundingClientRect?.();
      if (!at?.height) return stopScroll();
      const band = 56, speed = 14;
      const near = held.lastY - at.top, far = at.bottom - held.lastY;
      held.lean = near < band ? -Math.ceil((band - Math.max(0, near)) / band * speed)
        : far < band ? Math.ceil((band - Math.max(0, far)) / band * speed) : 0;
      if (held.lean && !scrollFrame.current) scrollFrame.current = window.requestAnimationFrame(scrollStep);
      if (!held.lean && scrollFrame.current) stopScroll();
    };
    const move = (next) => {
      if (gesture.current !== held) return;
      held.lastX = next.clientX || 0; held.lastY = next.clientY || 0;
      if (!held.live) {
        const dx = held.lastX - held.x, dy = held.lastY - held.y;
        if (dx * dx + dy * dy < 16) return;
        held.live = true; aim();
      } else aim();
      placeGhost(held.lastX, held.lastY); lean();
    };
    const unhook = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel); stopScroll();
    };
    const finish = () => {
      const current = dragRef.current;
      const next = current && favoriteReorder(ids, held.id, current.over, current.edge);
      if (!next) { gesture.current = null; setDrag(null); return; }
      saveOrder(next);
      setDrag({ ...current, settling: true });
      window.requestAnimationFrame(() => {
        const gap = zone.current?.querySelector('[data-md-favorite-gap]')?.getBoundingClientRect?.();
        const node = ghost.current;
        if (gap && node) {
          node.style.transition = `transform ${FAVORITE_SETTLE_MS}ms ${FAVORITE_EASE}`;
          node.style.transform = `translate3d(${gap.left - held.base.left}px, ${gap.top - held.base.top}px, 0)`;
        }
      });
      window.setTimeout(() => { if (gesture.current === held) { gesture.current = null; setDrag(null); } }, FAVORITE_SETTLE_MS + 35);
    };
    const up = () => { unhook(); if (gesture.current !== held) return;
      if (!held.live) { gesture.current = null; setDrag(null); return; } finish(); };
    const cancel = () => { unhook(); if (gesture.current === held) { gesture.current = null; setDrag(null); } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
  };

  const gap = drag ? <div data-md-favorite-gap="true" aria-hidden="true" style={{ height: drag.height,
    boxSizing: 'border-box', marginBottom: 11, borderRadius: 20, pointerEvents: 'none',
    background: mdRgbaF(accent, mdIsLightF() ? .07 : .1), border: `1px dashed ${mdRgbaF(accent, .38)}` }} /> : null;

  return <div ref={zone} data-md-favorites-list="true" data-md-drop-zone="favorites">
    {flow.flatMap((row, index) => {
      const originalIndex = rows.findIndex((item) => item.target === row.target);
      const landing = drag?.over === row.target ? (drag.edge || 'before') : null;
      const card = <div key={row.target} style={{ marginBottom: 11 }}>
        <FavoriteCard row={row} accent={accent} moving={false} landing={landing}
          onFavorite={() => onFavorite(row.target)} onOpen={() => onOpen(row)}
          onMoveStart={(event) => begin(event, row)} onMoveKey={(event) => moveKey(row, originalIndex, event)} />
      </div>;
      return gapAt === index ? [gap, card] : [card];
    })}
    {gapAt >= flow.length ? gap : null}
    {drag && <div ref={ghost} data-md-favorite-ghost="true" aria-hidden="true" style={{ position: gesture.current?.page ? 'fixed' : 'absolute',
      insetInlineStart: 0, top: 0, zIndex: 70, pointerEvents: 'none', boxSizing: 'border-box',
      opacity: drag.settling ? .98 : 1, filter: 'drop-shadow(0 18px 26px rgba(0,0,0,.22))' }} />}
  </div>;
}

/* `open` is a PROP, and it has to be. Written as a bare `open` inside this
   component it is not the surface's state at all — it resolves up the scope
   chain to `window.open`, a function, so the guard below could never say no.
   The surface's openness lives 500 lines down in MdFinder; the only way this
   field can be asked about it is to be handed it. */
function SearchField({ inputRef, text, setText, onClear, onKeyDown, onFocus, active, accent, open }) {
  const light = mdIsLightF();
  return <div data-md-search-dock="true" style={{ display: 'flex', alignItems: 'center', gap: 9,
    height: 50, boxSizing: 'border-box', margin: '10px 12px', padding: '0 12px', flexShrink: 0, borderRadius: 14,
    /* A hairline in the dark, none in the light (Marwan, 2026-08-24, having
       seen it borderless in both). Pure black on a near-black panel has no
       edge of its own to be found by; opaque white on the light frost already
       has one, and without the line it matches the shortcut tiles above it,
       which have been borderless all along. */
    background: light ? '#fff' : '#000',
    border: light ? 'none' : `1px solid ${ink(.16)}` }}>
    <span data-md-search-leading="true" style={{ width: 28, height: 28, minWidth: 28, flex: '0 0 28px',
      display: 'grid', placeItems: 'center' }}>
      {/* The × erases the QUERY, not the search (polish 7). preventDefault on
          the press: a press that took focus would put the phone's keyboard
          down before the click ever fired — the caret stays in the field. */}
      {active ? <button onClick={onClear} onPointerDown={(e) => e.preventDefault()}
        onMouseDown={(e) => e.preventDefault()} aria-label="Clear search"
        style={{ width: 28, height: 28, boxSizing: 'border-box', padding: 4, border: 0,
          background: 'transparent', color: ink(.7), cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
        {window.MDI.close('currentColor', 15)}</button> : <FindMark color={ink(.42)} />}
    </span>
    <input ref={inputRef} value={text} onChange={(e) => { setText(e.target.value); if (open) mdTabsRememberReturn(location.href, { text: e.target.value }); }} onKeyDown={onKeyDown} onFocus={onFocus} aria-label="Search everything"
      placeholder="Search everything…" autoComplete="off" autoCorrect="off" spellCheck="false"
      style={{ flex: 1, minWidth: 0, border: 0, outline: 0, background: 'transparent', color: ink(.94),
        caretColor: mdRgbaF(accent, 1), font: 'inherit', fontSize: 16.5 }} />
  </div>;
}

const MD_TABS_RETURN = 'md:tabs-return:v1';
function mdTabsReturnCurrent() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(MD_TABS_RETURN) || 'null');
    const here = location.pathname + location.search + location.hash;
    return !!(saved && saved.href === here && Date.now() - Number(saved.at || 0) < 12 * 60 * 60 * 1000);
  } catch (_error) { return false; }
}
function mdTabsRememberReturn(href, extra = null) {
  try {
    const url = new URL(href || location.href, location.href);
    const from = location.pathname + location.search + location.hash;
    sessionStorage.setItem(MD_TABS_RETURN, JSON.stringify({
      href: url.pathname + url.search + url.hash, from,
      scroll: Math.max(0, window.scrollY || 0), at: Date.now(),
      /* The query the surface was holding, so a reload can hand it back. The
         bare Tabs survives nothing today: its 12h return only fires when a
         destination was chosen, and a refresh while browsing dropped both the
         surface and whatever was typed into it. Remembering the text here
         costs nothing and is the difference between "still where I was" and
         "start the search over" after Safari evicts the tab. */
      text: typeof extra?.text === 'string' ? extra.text : '',
    }));
  } catch (_error) {}
}
function mdTabsReturnText() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(MD_TABS_RETURN) || 'null');
    return typeof saved?.text === 'string' ? saved.text : '';
  } catch (_error) { return ''; }
}
function mdTabsReturnScroll() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(MD_TABS_RETURN) || 'null');
    return Number.isFinite(saved?.scroll) ? Math.max(0, saved.scroll) : null;
  } catch (_error) { return null; }
}
function mdTabsForgetReturn() {
  try { sessionStorage.removeItem(MD_TABS_RETURN); } catch (_error) {}
}

function MdFinder({ accent, onGo, onBoards, next = null, nowSec = 0, canBoards = false,
  recordCtx = null, sidebarToggle = null,
  /* N-8: openness belongs to the scene. `standing` is the scene's word, the
     doors below flip `open` optimistically and announce through `onStand` /
     `onDismiss`, and the effect further down converges on any change the APP
     decided (a history walk landing on the Tabs entry, a rule retiring it).
     `covered` is a boot-time fact only: whether something already stands
     over a restored Tabs, so the surface must not paint for even a frame.
     All three default to null so the surface still works standalone. */
  standing = null, onStand = null, onDismiss = null, covered = null }) {
  const L = window.useMdLayout ? window.useMdLayout() : { phone: true, fine: false };
  const light = mdIsLightF();
  const [open, setOpen] = useStateF(() => (standing === null ? mdTabsReturnCurrent() : !!standing));
  const [text, setText] = useStateF(() => mdTabsReturnText());
  const [hidden, setHidden] = useStateF(() => (covered === null ? mdTabsReturnCurrent() : !!covered));
  const [handoff, setHandoff] = useStateF(false);
  const [sideDestination, setSideDestination] = useStateF(false);
  const [catalog, setCatalog] = useStateF([]), [rows, setRows] = useStateF([]);
  const [asked, setAsked] = useStateF(false), [sel, setSel] = useStateF(0);
  const [kind, setKind] = useStateF('All'), [last, setLast] = useStateF('anytime'), [sort, setSort] = useStateF('best');
  const [filterOpen, setFilterOpen] = useStateF(false);
  const [recentsOpen, setRecentsOpen] = useStateF(true), [favoritesOpen, setFavoritesOpen] = useStateF(true);
  const [facet, setFacet] = useStateF({ part: '', project: '', database: '' });
  const [nav, setNav] = useStateF(() => window.mdNavigationRead ? window.mdNavigationRead() : null);
  const inputRef = useRefF(null), surfaceRef = useRefF(null), previousFocus = useRefF(null), timer = useRefF(null), ticket = useRefF(null);
  const handoffTimer = useRefF(null);
  const textRef = useRefF(text); textRef.current = text;
  const phoneScroll = useRefF({ day: 0, tabs: 0 });
  const leavingPage = useRefF(false);
  const activeRef = useRefF(false), filterRef = useRefF(false);
  if (!ticket.current) ticket.current = window.mdFindTicket();
  const active = !!text.trim();
  activeRef.current = active; filterRef.current = filterOpen;
  const focusSearch = () => {
    /* Focusing the search field must not make Safari choose a new page offset.
       The field is already visible — it leads the sticky top chrome, so there
       is nothing for the browser to scroll into view — and preventScroll keeps
       the Tabs document exactly where it was while still raising the keyboard. */
    const pageY = L.phone ? phoneScroll.current.tabs : null;
    try { inputRef.current?.focus({ preventScroll: true }); }
    catch { inputRef.current?.focus(); }
    /* Older WebKit builds can ignore preventScroll for an input in a freshly
       portalled page. Correct that single focus side effect on the next paint;
       subsequent finger and momentum scrolling remain entirely native. */
    if (pageY != null) requestAnimationFrame(() => {
      window.scrollTo(0, pageY);
    });
  };
  const close = () => { if (L.phone) phoneScroll.current.tabs = Math.max(0, window.scrollY || 0);
    leavingPage.current = false;
    /* "Still where I was", not "start the search over". A session-level close
       (Escape, ×, or stepping to a destination) keeps the query against the
       return marker so reopening hands it straight back; only an explicit
       shut or a show that consumed it drops it. The
       field is still cleared visually, and the marker is rewritten below from
       the ref the input keeps fresh — never from the empty string we just set. */
    const rememberedText = textRef.current;
    mdTabsRememberReturn(location.href, { text: rememberedText });
    clearTimeout(handoffTimer.current); setOpen(false); setHidden(false); setHandoff(false); setSideDestination(false); setText(''); setRows([]); setKind('All');
    setLast('anytime'); setSort('best'); setFacet({ part: '', project: '', database: '' });
    setFilterOpen(false); };
  /* The bare Tabs is worth coming back to on its own, not only as the road
     to a destination. Its return marker used to fire only once a destination
     was chosen, so a refresh while simply browsing landed on Today instead
     of back here. Remember the surface the moment it opens; the destination
     path rewrites the same key with its own scroll, so nothing double-books.
     Reads the live query through a ref so the empty-string reset inside a
     close never overwrites it with a marker of its own. */
  const rememberBareTabs = () => mdTabsRememberReturn(location.href, { text: textRef.current });
  /* Standing is announced to the scene, which pushes the one history entry
     Tabs gets (via the journal in md-app). The surface writes no history of
     its own any more. */
  const enterTabs = () => { onStand?.(); rememberBareTabs(); };
  /* The surface's own openers and its boot restore seed its query from the
     return marker, so a close that wipes the field is never the end of what
     was typed — the next open hands it back. One reader for all three. */
  const restoreRememberedText = () => { const t = mdTabsReturnText(); if (t) setText(t); };
  /* Escape must close in one press: the surface closes itself FIRST, then
     tells the scene, whose journal reconciles the browser's stack after (the
     D1 law — never wait on a history walk WebKit may swallow). */
  const exitTabs = () => { close(); onDismiss?.(); };
  const searchBack = () => { setText(''); setKind('All'); setLast('anytime'); setSort('best'); setFacet({ part: '', project: '', database: '' }); setFilterOpen(false);
    inputRef.current?.blur(); if (document.activeElement?.blur) document.activeElement.blur(); };
  /* The field's × erases only the QUERY (polish 7): browse comes back because
     `active` follows the text, but the caret stays put and the filters keep
     their settings. Escape remains the full retreat — that is searchBack. */
  const searchClear = () => { setText(''); if (open) mdTabsRememberReturn(location.href, { text: '' }); };

  useEffectF(() => {
    const key = (e) => { if (!e.defaultPrevented && window.mdFindAsks(e)) { e.preventDefault(); enterTabs(); setOpen(true);
      setTimeout(focusSearch, 20); } };
    const find = () => { clearTimeout(handoffTimer.current); leavingPage.current = false; restoreRememberedText(); enterTabs(); setOpen(true); setHidden(false); setHandoff(false); setSideDestination(false); setTimeout(focusSearch, 20); };
    const shut = () => { close(); mdTabsForgetReturn(); };
    const hide = (event) => { clearTimeout(handoffTimer.current);
      /* Re-entrant since 2026-08-24: the App RESTATES this event whenever the
         scene changes what the shroud should say (the desk watcher in
         md-app). Only the FIRST hide finds Tabs' own scroll under the window
         — a restatement arrives with the destination's page standing, and
         snapshotting that would hand it back as Tabs' place on return. */
      if (L.phone && !leavingPage.current) phoneScroll.current.tabs = Math.max(0, window.scrollY || 0);
      leavingPage.current = true;
      setHandoff(event?.detail?.coverDay !== false); setHidden(true); setSideDestination(false); };
    const show = (event) => { clearTimeout(handoffTimer.current);
      const returnY = mdTabsReturnScroll();
      const returnText = mdTabsReturnText();
      /* Scroll and query only — restoring the URL is the browser's job now:
         the journal walked (or will walk) the real entry back (N-8). */
      mdTabsForgetReturn();
      if (returnText) setText(returnText);
      setHidden(false); setSideDestination(!!event?.detail?.sideDestination);
      if (L.phone) {
        const y = returnY == null ? phoneScroll.current.tabs : returnY;
        phoneScroll.current.tabs = y;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          window.scrollTo(0, y); leavingPage.current = false;
        }));
      }
      const delay = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 360;
      handoffTimer.current = setTimeout(() => setHandoff(false), delay); };
    const change = (e) => setNav(e.detail || window.mdNavigationRead());
    window.addEventListener('keydown', key);
    window.addEventListener('mdfindopen', find); window.addEventListener('mdnavigationchange', change);
    window.addEventListener('mdtabsclose', shut);
    window.addEventListener('mdtabshide', hide); window.addEventListener('mdtabsshow', show);
    return () => { window.removeEventListener('keydown', key);
      window.removeEventListener('mdfindopen', find); window.removeEventListener('mdnavigationchange', change);
      window.removeEventListener('mdtabsclose', shut);
      window.removeEventListener('mdtabshide', hide);
      window.removeEventListener('mdtabsshow', show); clearTimeout(handoffTimer.current); };
  }, []);
  /* The scene's word arrives here (N-8). The doors above flip `open` first
     and announce, so the common case finds nothing to do; what this effect
     truly carries is the APP-decided change — a Back or Forward landing on
     (or leaving) the Tabs entry, a registry rule retiring the surface — with
     the same restore and reset those doors perform. It converges on
     TRANSITIONS of the scene's word, deliberately: a purely visual local
     close under a surface still standing above is presentation, and the
     stack alone remains the truth of what stands. */
  const standingWas = useRefF(standing);
  useEffectF(() => {
    if (standing === null || standing === standingWas.current) return;
    standingWas.current = standing;
    if (standing && !open) {
      clearTimeout(handoffTimer.current); leavingPage.current = false;
      restoreRememberedText(); rememberBareTabs(); setOpen(true); setHidden(false);
      setHandoff(false); setSideDestination(false);
    } else if (!standing && open) close();
  }, [standing]);
  useEffectF(() => {
    if (!open) return;
    previousFocus.current = document.activeElement;
    if (window.mdStorageFlush) window.mdStorageFlush();
    return () => {
      const back = previousFocus.current;
      if (back?.focus && document.contains(back)) setTimeout(() => {
        try { back.focus({ preventScroll: true }); }
        catch { back.focus(); }
      }, 0);
    };
  }, [open]);
  /* Phone Tabs is a genuine document page. The day root is kept mounted as a
     fixed layer behind it, while this portal is the sole in-flow child that
     gives body its scroll height. Opening/closing restores each page's own
     position once; no scroll event ever mirrors one scroller into another. */
  useLayoutEffectF(() => {
    if (!open || !L.phone) return undefined;
    const root = document.documentElement;
    phoneScroll.current.day = Math.max(0, window.scrollY || 0);
    root.setAttribute('data-md-phone-tabs-page', 'true');
    const target = phoneScroll.current.tabs;
    let restoring = true;
    let finishRestoring = null;
    window.scrollTo(0, target);
    let settleAfterLayout = null;
    const settle = requestAnimationFrame(() => {
      window.scrollTo(0, target);
      /* The portal and the collapsed day root can finish their layout in
         adjacent frames. A final one-shot restore after that handoff is scroll
         restoration, not scroll mirroring, and leaves native momentum alone. */
      settleAfterLayout = requestAnimationFrame(() => {
        window.scrollTo(0, target);
        /* Keep transient scroll events from the root handoff and WebKit's
           focus pass from replacing the saved offset. The guard ends after
           the focus task, then records the actual settled native position. */
        finishRestoring = setTimeout(() => {
          restoring = false;
          phoneScroll.current.tabs = Math.max(0, window.scrollY || 0);
        }, 80);
      });
    });
    const remember = () => {
      if (!restoring && !leavingPage.current && surfaceRef.current?.style.visibility !== 'hidden') {
        phoneScroll.current.tabs = Math.max(0, window.scrollY || 0);
      }
    };
    window.addEventListener('scroll', remember, { passive: true });
    return () => {
      cancelAnimationFrame(settle);
      if (settleAfterLayout != null) cancelAnimationFrame(settleAfterLayout);
      if (finishRestoring != null) clearTimeout(finishRestoring);
      window.removeEventListener('scroll', remember);
      root.removeAttribute('data-md-phone-tabs-page');
      const day = phoneScroll.current.day;
      window.scrollTo(0, day);
      requestAnimationFrame(() => window.scrollTo(0, day));
    };
  }, [open, L.phone]);
  /* Sticky keeps the phone chrome attached to the document scroller. iOS can
     additionally PAN the visual viewport without changing the layout viewport
     sticky is measured against, which would leave the top chrome above the
     visible area; publishing that offset as a variable glues it back on.

     Only the top offset is published. A bottom inset was published too until
     Polish 6, and it was the keyboard's own height — the one measurement that
     changes the instant somebody starts typing. Nothing is anchored to the
     bottom of this surface any more, so the keyboard has nothing to push:
     it opens over the page and closes off it. This moves chrome, never
     content, and never the scroll position.

     And the pan is BOUNDED BY THE KEYBOARD THAT CAUSED IT. Safari also reports
     a nonzero offsetTop with no keyboard at all — mid rubber-band, and while a
     scroll-to-dismiss pan unwinds — and gluing the chrome to that made it drift
     down the page during a plain scroll. That was answered with a threshold,
     "is more than 120px of viewport gone", and a threshold can flip mid-gesture:
     drag with the keyboard up and Safari reports the viewport growing back as
     the keyboard starts to leave, so the answer changed between frames and the
     chrome jumped to the layout top and away again. Marwan recorded the bounce.

     A pan exists to reveal what the keyboard covers, so it can never usefully
     exceed what the keyboard hides. Clamping to that height says the same thing
     continuously: with no keyboard the hidden height is zero, so a rubber-band
     publishes zero by construction rather than by a number someone chose, and
     as the keyboard leaves the two fall together with no frame between them. */
  useLayoutEffectF(() => {
    if (!open || !L.phone) return undefined;
    const root = document.documentElement;
    const vv = window.visualViewport;
    let frame = null;
    const measure = () => {
      frame = null;
      const hidden = vv ? Math.max(0, Math.round(window.innerHeight - vv.height)) : 0;
      const pan = vv ? Math.max(0, Math.round(vv.offsetTop || 0)) : 0;
      root.style.setProperty('--md-tabs-visual-top', `${Math.min(pan, hidden)}px`);
    };
    const schedule = () => { if (frame == null) frame = requestAnimationFrame(measure); };
    measure();
    window.addEventListener('resize', schedule);
    vv?.addEventListener('resize', schedule); vv?.addEventListener('scroll', schedule);
    /* A frame iOS dropped while this screen was away would hold the latch for
       the life of the page, and the inset would keep the keyboard's last known
       height over a keyboard that is no longer there. See mdOnPageResume. */
    const resumed = window.mdOnPageResume(() => {
      if (frame != null) { cancelAnimationFrame(frame); frame = null; }
      measure();
    });
    return () => {
      if (frame != null) cancelAnimationFrame(frame);
      resumed();
      window.removeEventListener('resize', schedule);
      vv?.removeEventListener('resize', schedule); vv?.removeEventListener('scroll', schedule);
      root.style.removeProperty('--md-tabs-visual-top');
    };
  }, [open, L.phone]);
  /* ── LETTING GO OF THE KEYBOARD ─────────────────────────────────────────────
     Marwan, with a recording (2026-08-28): "the sticky header bounces and jitter
     and scrolles out of view when i scroll while the keyboard is raised" — and,
     on a tablet, "the whole side panel + the opened window scrolls".

     Both are one state: a finger dragging while the keyboard holds part of the
     screen. Everything the chrome does then rests on a number JavaScript has to
     chase — `visualViewport.offsetTop`, which Safari reports late during
     momentum and not at all during some pans — so the chrome is placed against a
     measurement that is already stale. The clamp above bounds the damage; it
     cannot fix it, because the value simply arrives after the frame that needed
     it.

     So the state ends instead. A drag lets the keyboard go — what iOS does
     everywhere else, `keyboardDismissMode = .onDrag` — and the viewport returns
     to full height, the pan to zero, and the chrome to a plain `top: 0` sticky
     the compositor places without help. A drag that BEGINS in the field is the
     user working the field, and keeps its keyboard.

     Safari's bar capsule is untouched by this: the phone page still scrolls the
     document, which is the whole of what shrinks the bar, and Safari will not
     collapse it while a field holds focus anyway. Letting go is what lets the
     capsule shrink on a page that was searched. */
  useEffectF(() => {
    if (!open) return undefined;
    let fromField = false;
    const start = (e) => {
      const t = e.target;
      fromField = !!(t?.closest && t.closest('[data-md-search-dock]'));
    };
    const drag = () => {
      if (fromField) return;
      const el = document.activeElement;
      if (el?.blur && surfaceRef.current?.contains(el)
        && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) el.blur();
    };
    window.addEventListener('touchstart', start, { passive: true });
    window.addEventListener('touchmove', drag, { passive: true });
    return () => {
      window.removeEventListener('touchstart', start);
      window.removeEventListener('touchmove', drag);
    };
  }, [open]);
  /* Tabs fills the day sheet edge-to-edge, so while it is the thing being
     looked at, the day beneath it must not paint. Two translucent materials
     composite the tint twice — most visibly in light mode, where Tabs looked
     greyer than the otherwise identical Projects window.

     ON SHOW, NOT MERELY MOUNTED, and the difference is a real defect. This
     mark used to be keyed on `open` alone and used to blank the SHARED sheet's
     own fill. But a window hides Tabs without unmounting it — `hidden` below —
     and the sheet then shows the DAY, which was left with no material: reload
     a desktop with a window open, lean on the edge to peek, and the desk
     painted straight through the day until Search was opened and closed.
     Marwan found it, 2026-08-28.

     So the material lives on the day's own surface now (md-app.jsx), and this
     says only "Tabs is the surface on show" — the same answer the day is made
     inert by, just below. Before paint, so the day never shows for a frame. */
  useLayoutEffectF(() => {
    /* Bounded panels only. On a phone Tabs portals to body, not into the day
       sheet — replacing the day there strips the material it keeps behind the
       backstop and deepens the bleed this was meant to cure. */
    if (!open || hidden || L.phone) return;
    const panel = document.getElementById('md-day-sidebar');
    if (!panel) return;
    panel.setAttribute('data-md-tabs-on-show', 'true');
    return () => panel.removeAttribute('data-md-tabs-on-show');
  }, [open, hidden, L.phone]);
  /* Tabs' whole dismissal ladder — filter, then search, then the surface — is
     ONE layer on the dismiss arbiter: the arbiter decides WHO is on top, the
     surface decides WHAT a dismissal means for itself. It owns every field
     (the search box exists only while Tabs does), so one press steps the
     ladder rather than merely blurring. The layer stands only while Tabs is
     the visible surface: a destination above it, or a docked destination
     beside it, holds its own layer instead. */
  window.useMdDismissLayer(() => {
    if (filterRef.current) setFilterOpen(false); else if (activeRef.current) searchBack(); else exitTabs();
  }, open && !hidden && !sideDestination, () => true);
  /* A destination hides Tabs without unmounting it. The visible layer alone
     makes Today inert; keeping that active while a native destination is
     above it would make that destination unusable. */
  useEffectF(() => {
    if (!open || hidden) return;
    const underneath = [...document.querySelectorAll('main[aria-label="Your day"], [data-md-today-header="true"]')];
    for (const node of underneath) { node.setAttribute('inert', ''); node.setAttribute('aria-hidden', 'true'); }
    return () => { for (const node of underneath) { node.removeAttribute('inert'); node.removeAttribute('aria-hidden'); } };
  }, [open, hidden]);
  useEffectF(() => {
    if (!open) return;
    let cancelled = false;
    const id = setTimeout(() => { if (!cancelled) setCatalog(buildCatalog(recordCtx)); }, 0);
    return () => { cancelled = true; clearTimeout(id); };
  }, [open, recordCtx]);
  useEffectF(() => {
    if (!open) return; clearTimeout(timer.current); setAsked(false);
    if (!active) { setRows([]); setSel(0); return; }
    timer.current = setTimeout(() => {
      const seq = ticket.current.ask();
      if (!ticket.current.standing(seq)) return;
      const q = text.trim().toLocaleLowerCase();
      const found = catalog.filter((row) => String(row.searchText || `${row.title}\n${row.snippet || ''}`)
        .toLocaleLowerCase().includes(q));
      found.sort((a, b) => {
        const rank = (row) => { const title = String(row.title || '').toLocaleLowerCase();
          return title === q ? 0 : title.startsWith(q) ? 1 : title.includes(q) ? 2 : 3; };
        return rank(a) - rank(b) || String(a.title).localeCompare(String(b.title));
      });
      setRows(found); setSel(0); setAsked(true);
    }, 70);
    return () => clearTimeout(timer.current);
  }, [text, open, catalog]);

  const favorites = window.mdNavigationFavorites ? window.mdNavigationFavorites(nav) : [];
  const favoriteSet = new Set(favorites.map((x) => x.target));
  const todayFajr = window.mdPrayersFor?.(0)?.find((p) => p.id === 'fajr');
  const boundary = window.mdNavigationCycleStart ? window.mdNavigationCycleStart(Date.now(), todayFajr?.min) : 0;
  const recentRows = (window.mdNavigationRecents ? window.mdNavigationRecents(nav, boundary) : [])
    .map((x) => titleFromTarget(x.target, recordCtx)).filter(Boolean);
  const favoriteRows = favorites.map((x) => titleFromTarget(x.target, recordCtx)).filter(Boolean);
  const visible = useMemoF(() => {
    let out = rows.filter((r) => (kind === 'All' || r.category === kind));
    for (const name of ['part', 'project', 'database']) if (facet[name]) out = out.filter((r) => r[name] === facet[name]);
    if (last !== 'anytime') { const days = last === 'fajr' ? 1 : Number(last); const since = last === 'fajr' ? boundary : Date.now() - days * 86400000;
      out = out.filter((r) => !r.writtenAt || r.writtenAt >= since); }
    if (sort === 'az') out = [...out].sort((a, b) => String(a.title).localeCompare(String(b.title)));
    if (sort === 'viewed') { const clocks = nav?.recents || {}; out = [...out].sort((a, b) => (clocks[b.target] || 0) - (clocks[a.target] || 0)); }
    if (sort === 'written') out = [...out].sort((a, b) => (b.writtenAt || 0) - (a.writtenAt || 0));
    return out.slice(0, 160);
  }, [rows, kind, last, sort, facet, nav, boundary]);
  const facetOptions = useMemoF(() => ({
    part: [...new Set(rows.map((r) => r.part).filter(Boolean))],
    project: [...new Set(rows.map((r) => r.project).filter(Boolean))],
    database: [...new Set(rows.map((r) => r.database).filter(Boolean))],
  }), [rows]);
  const toggleFavorite = (target) => window.mdNavigationSetFavorite && window.mdNavigationSetFavorite(target, !favoriteSet.has(target));
  const hideForDestination = (coverDay = true) => {
    if (document.activeElement?.blur) document.activeElement.blur();
    clearTimeout(handoffTimer.current);
    /* Snapshot before the flag rises, and only on the first leave — the
       same rule the mdtabshide handler holds, for the same reason. */
    if (L.phone && !leavingPage.current) phoneScroll.current.tabs = Math.max(0, window.scrollY || 0);
    leavingPage.current = true;
    setHandoff(coverDay);
    setSideDestination(false);
    setHidden(true);
  };
  const opensBesideTabs = (row) => {
    /* Project/Database windows, record sheets and the Desk canvas occupy the
       docked workspace to the right of the day panel. Hiding Tabs here was the
       visible bug: it exposed Today in the left panel even though the native
       destination had plenty of room beside it. Full-screen/overlay layouts
       still preserve Tabs underneath exactly as before. */
    if (!L.canvas || L.overlay) return false;
    return ['workshop', 'project', 'pursuit', 'domain', 'database',
      'view', 'record', 'board'].includes(row?.place?.go);
  };
  /* THE ONE DESTINATION THAT IS THE GROUND. On an upright tablet the day is an
     inset sheet floating over a full-bleed canvas, so opening a desk from here
     is not a push and not a hide — it is the panel getting out of the way of
     something already underneath it. Hiding Tabs instead was the bug Marwan
     hit: the surface hid itself for a destination it was standing on, so the
     panel kept its prayer header, lost every control inside it, and only a
     reload brought them back. The day owns the fold, so we ask it. */
  const foldsOverDestination = (row) =>
    L.canvas && L.overlay && row?.place?.go === 'board';
  const openRow = (row) => {
    /* Navigation is a push over Tabs, never a close. Its DOM stays mounted so
       query/filter/edit/disclosure state and the exact scroll offsets survive.
       The entry under the destination is Tabs' own journal entry — nothing
       needs marking here any more (N-8). */
    if (foldsOverDestination(row)) {
      setSideDestination(true);
      window.dispatchEvent(new CustomEvent('mddayfold'));
    } else if (opensBesideTabs(row)) setSideDestination(true);
    else {
      /* Carry the live query onto the destination's marker: a reload from
         there restores Tabs beneath it, and "still where I was" includes
         what was typed — not only that the surface stood. */
      mdTabsRememberReturn(window.mdNavigationHref?.(row.target) || location.href,
        { text: textRef.current });
      hideForDestination(row?.place?.go !== 'day');
    }
    if (!window.mdNavigationNavigate?.(row.target, { source: 'tabs' })) onGo?.(row.place, { ...row, source: 'tabs' });
  };
  const searchKeys = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((n) => visible.length ? (n + 1) % visible.length : 0); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSel((n) => visible.length ? (n - 1 + visible.length) % visible.length : 0); }
    if (e.key === 'Enter' && visible[sel]) { e.preventDefault(); openRow(visible[sel]); }
  };
  /* Desks uses the same stable target/navigation door as every other shortcut.
     Besides making the URL shareable, this is what lets a refreshed chooser
     rebuild the same Tabs return stack instead of becoming an orphan screen. */
  const openShortcut = (row) => openRow(row);

  if (!open) return null;
  /* On phone the surface must participate in body layout: that is what makes it
     a real Safari page. Wider layouts still belong inside the bounded day panel. */
  const host = L.phone ? document.body : (document.getElementById('md-day-sidebar') || document.body);
  const browseRows = favoriteRows;
  /* “Tabs” is the internal name for this navigation home. More remains the
     user-facing door and accessible name. The migration is done (N-13): one
     selector, one event vocabulary, and the seal test keeps it that way. */
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  /* During a panel-to-panel handoff this guard belongs below both complete
     surfaces, so Today can never leak if their React commits are separated by
     a frame. It does not animate and is covered atomically by either owner.

     On a phone it is also a backstop: Tabs is an in-flow document page over
     the day's fixed, opaque root, and rubber-banding past either edge slides
     the translucent surface off the viewport. Whatever that overscroll exposes
     must be sheet material, never the day.

     BUT ONLY WHILE TABS IS ON SCREEN TO BE OVERSCROLLED. `L.phone` alone
     raised this plate, which held while everything a phone could reach was
     either Tabs or a modal layer portalled ABOVE it. The desk is ground now —
     mounted inside the app root and painted BENEATH this portal — so a plate
     that stands whenever the layout is a phone stands over the desk, and
     choosing one from the chooser landed on a black screen with nothing but
     the countdown on it. `handoff` still covers the case this plate was born
     for: a hidden Tabs whose destination has asked to be covered. */
  const handoffGuard = (handoff || (L.phone && !hidden)) && <div aria-hidden="true"
    data-md-tabs-handoff={handoff ? 'true' : undefined}
    data-md-tabs-backstop="true" style={{
      position: L.phone ? 'fixed' : 'absolute', inset: 0, zIndex: 79, pointerEvents: 'none',
      background: light ? '#f8f9fa' : '#08090c',
    }} />;
  /* The mask that dissolves the blur at the bar's bottom edge — the same 14px
     the day card's fill dissolves over (md-today.jsx), so the two bands end the
     same way. Written once because WebKit still wants the prefix. */
  const plateFade = 'linear-gradient(180deg, #000 0%, #000 calc(100% - 14px), transparent 100%)';
  /* HIDDEN WITH GROUND UNDER IT. A phone and an upright tablet are the two
     layouts whose destination is drawn BENEATH this surface rather than over
     it — `overlay` (md-layout.jsx:83) is the tablet's name for exactly that
     geometry — and on both of them `visibility: hidden` is not enough: WebKit
     goes on compositing a promoted layer through it, which is how a second
     countdown came to tick over an upright iPad's day. Both states here hold
     no space worth giving back (fixed on a phone, absolutely positioned in the
     panel on a tablet), so the cure is the same one the phone already had. */
  const overGround = hidden && (L.phone || L.overlay);
  /* ── THE FROST, ONE LAYER OUT ────────────────────────────────────────────
     The material this surface is made of used to be worn BY the surface. It
     cannot be, because of one rule of the engine: **a backdrop-filter sees
     nothing that its own backdrop-filtered ancestor painted.** An element
     carrying a backdrop-filter becomes a backdrop root, and every filter
     inside it samples that root's ground rather than its contents.

     So while the frost sat on the surface, the bar's blur plate below was
     inert on every layout but a phone — where it worked only because the
     phone flattens this surface to the opaque void (index.html, the atomic
     rule) and so takes the backdrop root away. That is the whole reason the
     band read as beautiful on Marwan's iPhone and as a flat shade on his
     desktop: on the desktop there had never been a blur at all, only a fill.
     Measured in the Browser pane at 790x560 — rows under the bar stayed razor
     sharp at blur(40px), and dissolved the moment this line moved out here.

     Nothing about the material changes: the same mdFrostDress, the same box,
     painted one step below the surface instead of on it. The surface keeps
     its geometry byte-for-byte and gives up only the declaration that was
     blinding its own children.

     NOT ON A PHONE, where the atomic rule replaces this material with the
     void anyway; a plate here would put translucent glass over the desk. */
  const frost = !L.phone && window.mdFrostDress && <div data-md-tabs-frost="true" aria-hidden="true"
    style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
      /* the surface's own z, so it lands directly beneath it in both states
         and above everything the surface outranks today — it is earlier in
         the portal, so an equal z paints it first. */
      zIndex: hidden ? 80 : 390,
      ...window.mdFrostDress(light),
      visibility: hidden ? 'hidden' : 'visible',
      /* and the material is the louder half of the same trap: a
         backdrop-filtered plate is promoted by construction. */
      display: overGround ? 'none' : 'block',
      transition: 'none', animation: 'none' }} />;
  const surface = <div ref={surfaceRef} role="dialog" aria-modal={sideDestination ? undefined : 'true'} aria-label="More" aria-hidden={hidden || undefined}
    data-md-tabs-surface="true"
    /* THE SCROLLER SAYS SO WHERE THE DRAG LOOKS. Autoscrolling a dragged
       Favorite at the panel edge finds its scroller with
       `closest('.md-scroll')`, which returns the nearest ancestor carrying the
       class whether or not it actually scrolls — so the class belongs on
       whichever box has the overflow, and on a phone that is the document. */
    className={L.phone || hidden ? undefined : 'md-scroll'}
    data-md-panel-screen="atomic"
    data-md-side-destination={sideDestination || undefined}
    style={{ position: L.phone ? (hidden ? 'fixed' : 'relative') : 'absolute',
      inset: L.phone && hidden ? 0 : (L.phone ? undefined : 0),
      top: 0, width: '100%',
      height: L.phone ? (hidden ? '100dvh' : 'auto') : '100%',
      /* lvh, not dvh: the page must be at least as tall as the viewport with
         Safari's bar COLLAPSED, or a short Tabs page has nothing to scroll and
         the bar can never shrink — see the phone-document rules in index.html.
         The hidden state above stays a dvh viewport layer: it tracks what is
         visible behind a destination, it does not invite a scroll. */
      minHeight: L.phone ? (hidden ? 0 : '100lvh') : 0,
      zIndex: hidden ? 80 : 390,
      /* OUT OF LAYOUT, NOT MERELY INVISIBLE — wherever there is ground beneath
         it. WebKit goes on painting this box and everything in it after
         `visibility:hidden` below has been computed on the whole subtree; the
         countdown kept ticking in plain white over a live drawing until this
         line. Nothing is lost: neither hidden state holds space to give back,
         and state and scroll snapshots survive untouched. A DOCKED panel keeps
         its box — there the destination is drawn over Tabs, not under it. */
      display: overGround ? 'none' : 'flex', flexDirection: 'column',
      overflow: L.phone && !hidden ? 'visible' : 'hidden', color: ink(.92),
      /* ONE SCROLLER, AND THIS IS IT — on every layout that is not a phone,
         where the document is already the one. The chrome above used to sit in
         this flex column with the list scrolling inside its own box below, so
         nothing ever passed beneath the header and its bottom edge was a hard
         line. Written as a second declaration rather than folded into the
         shorthand above so the horizontal axis stays clipped and the hidden
         state's plain `overflow: hidden` is left exactly as it was. */
      /* AND THE DRAG STAYS IN IT. This box only became the scroller today; the
         list used to scroll in one of its own, so there was nothing to chain
         out of. A drag running past the end of the list now chains into the
         document — which on a tablet has nowhere to go, so iOS pans the visual
         viewport instead and the whole sheet AND the window beside it slide,
         which is what Marwan saw. Tabs' phone page already contains its own
         overscroll (index.html); this is the same sentence for the panel.
         NOT ON A PHONE, and that is deliberate: there the document is the
         scroller, and its scrolling is the entire reason Safari can shrink its
         bar capsule. Nothing here touches that page. */
      ...(L.phone || hidden ? null : { overflowY: 'auto', overscrollBehavior: 'contain' }),
      /* NO MATERIAL ON THE SURFACE ITSELF — see the frost plate above, which
         wears it one step below. A backdrop-filter here would make this box a
         backdrop root and blind every filter inside it, the bar's included.
         On a phone the atomic rule outranks this line with the void. */
      background: 'transparent',
      paddingTop: L.phone ? 0 : 'var(--md-safe-top)', paddingBottom: L.phone ? 0 : 'var(--md-safe-bottom)',
      overflowAnchor: L.phone ? 'none' : undefined,
      visibility: hidden ? 'hidden' : 'visible',
      pointerEvents: hidden ? 'none' : 'auto',
      transition: 'none', animation: 'none' }}>
    {/* ── the top chrome, one group ──────────────────────────────────────────
        The countdown and the search field travel together, and on a phone the
        whole group is what sticks: it carries the frost, and it is the only
        thing tied to the visual viewport — by its TOP edge, which a pan moves
        and a keyboard does not.

        The field sat in a dock at the foot of the page until Polish 6. A
        bottom-anchored dock has to be told where the bottom is, and once the
        keyboard is open the bottom of the visual viewport IS the top of the
        keyboard — so the field, and everything laid out against it, jumped up
        on focus and fell back on blur. Nothing here is anchored to that edge
        now, so there is nothing left for a keyboard to move. */}
    <div data-md-tabs-top-chrome="true"
      style={{ position: 'sticky', top: L.phone ? 'var(--md-tabs-visual-top, 0px)' : 0,
        zIndex: 16, flexShrink: 0,
        /* NO FILL, IN ANY VIEW, and that is the whole of the shade promise.
           A fill is a second layer, and a second layer over a translucent
           surface always changes the shade. This bar wore two of them in one
           day: a flat 0.76 ground (a tint, a strip, a hard edge) and then the
           day card's own material — which on the DAY comes to nothing, because
           the card's frost is the colour of the day beneath it and the day is
           opaque, so 0.72 of C over C is C. Tabs on a desktop is glass over the
           workspace, so the same expression composited to something else and
           Marwan saw a band again within the hour.
           Nothing is painted here now, so matching the surface is a fact of
           construction rather than a value somebody has to keep in step. The
           effect lives on the plate below, which adds no colour either. */
        background: 'transparent' }}>
      {/* THE BLUR, AND ONLY THE BLUR. `inset:0` so it covers the bar, no fill
          so it cannot shift the shade, and `z-index:-1` so it paints above the
          surface's background but BELOW this bar's own writing — which is what
          keeps the softened text behind the countdown and the field rather
          than smeared across them. The mask dissolves it over the last 14px,
          so the band ends the way the day card's does instead of cutting.
          Marwan settled what it is for: "it doesn't have to completely hide the
          scrolled content behind it, it have a touch of blur that doesn't
          bleed into its content."
          This does nothing at all unless the surface above stays free of a
          backdrop-filter of its own — that is what the frost plate is for.
          NOT WHILE HIDDEN. WebKit promotes a backdrop-filtered box to its own
          composited layer and paints it without consulting the visibility it
          inherited, so a hidden Tabs would smear this across the desk — the
          same trap the surface itself pays for. See tests/tabs-backstop.js. */}
      {!hidden && <div data-md-tabs-blur-plate="true" aria-hidden="true"
        style={{ position: 'absolute', inset: 0, zIndex: -1, pointerEvents: 'none',
          backdropFilter: 'blur(18px) saturate(150%)',
          WebkitBackdropFilter: 'blur(18px) saturate(150%)',
          maskImage: plateFade, WebkitMaskImage: plateFade }} />}
      {/* The search dock already contributes 10px above itself. Twelve pixels
          above the countdown and two below its header make the visible group
          optically equidistant from the panel edge and the search field. */}
      <header data-md-tabs-countdown-header="true"
        style={{ position: 'relative', display: 'flex', alignItems: 'center', minHeight: 56,
          padding: L.phone ? 'var(--md-top-inset) 10px 2px 16px' : '12px 10px 2px 16px',
          flexShrink: 0 }}>
        <div style={{ flex: 1 }}>{window.PrayerCountdown
          ? <window.PrayerCountdown accent={accent} next={next} nowSec={nowSec} action={sidebarToggle} />
          : <div data-prayer-countdown="" style={{ fontSize: 22, fontWeight: 660 }}>{next?.name || 'Prayer times unavailable'}</div>}</div>
        <window.MdDismiss label="Close More" onClick={exitTabs}
          style={{ marginInlineEnd: -2 }} />
      </header>
      <SearchField inputRef={inputRef} text={text} setText={setText} onClear={searchClear} onKeyDown={searchKeys} active={active} accent={accent} open={open} />
    </div>
    {/* These three boxes take the phone's arrangement in every view now: they
        grow to their content and the surface above scrolls, so the results pass
        under the sticky chrome instead of being clipped at their own top edge
        62px below it. Three ternaries came out rather than went in. */}
    {active ? <div data-md-search-results="true" style={{ flex: '0 0 auto', minHeight: 0, display: 'flex', flexDirection: 'column',
      animation: reduceMotion ? 'none' : 'mdSearchContentIn .18s ease-out' }}>
      <div style={{ position: 'relative', display: 'flex', gap: 7, alignItems: 'center', padding: '2px 12px 9px', flexWrap: 'wrap' }}>
        <button onClick={() => setFilterOpen((v) => !v)} aria-expanded={filterOpen} style={chip()}>Filter</button>
        <select aria-label="Sort results" value={sort} onChange={(e) => setSort(e.target.value)} style={chip()}>
          <option value="best">Best match</option><option value="viewed">Recently viewed</option>
          <option value="written">Recently written</option><option value="az">A–Z</option>
        </select>
        {kind !== 'All' && <button onClick={() => setKind('All')} style={chip()}>{kind} ×</button>}
        {last !== 'anytime' && <button onClick={() => setLast('anytime')} style={chip()}>Last written ×</button>}
        {Object.entries(facet).filter(([, v]) => v).map(([name, value]) => <button key={name} onClick={() => setFacet((old) => ({ ...old, [name]: '' }))} style={chip()}>{value} ×</button>)}
        {(kind !== 'All' || last !== 'anytime' || sort !== 'best' || Object.values(facet).some(Boolean)) && <button onClick={() => { setKind('All'); setLast('anytime'); setSort('best'); setFacet({ part: '', project: '', database: '' }); }} style={{ ...chip(), marginInlineStart: 'auto' }}>Clear all</button>}
        {filterOpen && <div role="dialog" aria-label="Search filters" style={{ position: 'absolute', top: 38, insetInlineStart: 12, zIndex: 5,
          width: 250, padding: 12, borderRadius: 14, background: light ? '#fff' : '#181a1f', boxShadow: '0 18px 60px rgba(0,0,0,.3)' }}>
          <b style={{ fontSize: 12.5 }}>Kind</b><div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, margin: '8px 0 14px' }}>
            {['All', ...KIND_OPTIONS].map((k) => <button key={k} onClick={() => setKind(k)} style={{ ...chip(), color: kind === k ? mdRgbaF(accent, 1) : ink(.65) }}>{k} ({k === 'All' ? rows.length : rows.filter((r) => r.category === k).length})</button>)}
          </div><b style={{ fontSize: 12.5 }}>Last written</b><div style={{ display: 'grid', gap: 4, marginTop: 7 }}>
            {[['fajr', 'Current Fajr day'], ['7', '7 days'], ['30', '30 days'], ['anytime', 'Anytime']].map(([v, label]) =>
              <button key={v} onClick={() => setLast(v)} style={{ ...chip(), textAlign: 'start', color: last === v ? mdRgbaF(accent, 1) : ink(.65) }}>{label}</button>)}
          </div>
          {Object.entries(facetOptions).filter(([, values]) => values.length).map(([name, values]) => <div key={name} style={{ marginTop: 13 }}>
            <b style={{ fontSize: 12.5 }}>{name === 'part' ? 'Part of Life' : name[0].toUpperCase() + name.slice(1)}</b>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7 }}>{values.map((value) =>
              <button key={value} onClick={() => setFacet((old) => ({ ...old, [name]: value }))} style={chip()}>{value} ({rows.filter((r) => r[name] === value).length})</button>)}</div>
          </div>)}
        </div>}
      </div>
      <div aria-live="polite" style={{ padding: '0 16px 7px', color: ink(.38), fontSize: 12 }}>{visible.length} {visible.length === 1 ? 'result' : 'results'}</div>
      <div role="listbox" aria-label="Search results" style={{ flex: '0 0 auto',
        overflowY: 'visible', minHeight: 0,
        /* The page ends here now that the dock is gone, so it spends the
           bottom device inset the dock used to — without it the last result
           sits under the home indicator. */
        padding: L.phone ? '0 8px calc(var(--md-safe-bottom) + 16px)' : '0 8px 16px' }}>
        {visible.map((row, i) => <LinearRow key={row.target} row={row} active={i === sel} accent={accent} mobile={!L.fine}
          favorite={favoriteSet.has(row.target)} onFavorite={() => toggleFavorite(row.target)} onOpen={() => openRow(row)} />)}
        {!visible.length && <div style={{ padding: 42, textAlign: 'center', color: ink(.4) }}>{asked ? `Nothing holds “${text.trim()}”` : 'Searching…'}</div>}
      </div>
    </div> : <div style={{ flex: 1, overflowY: 'visible',
      minHeight: 0, padding: L.phone ? '4px 12px calc(var(--md-safe-bottom) + 18px)' : '4px 12px 18px' }}>
      <section aria-label="Shortcuts" style={{ paddingTop: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 6 }}>
          {FIXED.map((row) => <button key={row.target} onClick={() => openShortcut(row)} style={{ border: 0, borderRadius: 13,
            padding: '11px 3px 9px', background: light ? '#fff' : ink(.055), color: ink(.72), display: 'grid', justifyItems: 'center', gap: 6, cursor: 'pointer', fontSize: 10.5 }}>
            <FindGlyph go={row.place.go} category={row.category} color={ink(.58)} /><span>{row.title}</span></button>)}
        </div>
      </section>
      <section aria-label="Recents" style={{ marginTop: 20 }}><DisclosureBar icon="clock" label="Recents" open={recentsOpen} setOpen={setRecentsOpen} />
        {recentsOpen && <div data-md-recents-list="true" className="md-scroll"
          style={{ maxHeight: L.phone ? 'none' : 520, overflowY: L.phone ? 'visible' : 'auto' }}>
          {recentRows.length ? recentRows.map((row) => <LinearRow key={row.target} row={row} accent={accent} mobile={!L.fine}
            favorite={favoriteSet.has(row.target)} onFavorite={() => toggleFavorite(row.target)} onOpen={() => openRow(row)} />)
            : <div style={emptyText()}>Places you open during this Fajr day will appear here.</div>}
        </div>}
      </section>
      <section aria-label="Favorites" style={{ marginTop: 22 }}>
        <DisclosureBar icon="starLine" label="Favorites" open={favoritesOpen} setOpen={setFavoritesOpen} />
        {favoritesOpen && (browseRows.length
          ? <FavoriteStack rows={browseRows} accent={accent} onFavorite={toggleFavorite} onOpen={openRow} />
          : <div style={emptyText()}>Star a place to keep its live preview here on every device.</div>)}
      </section>
    </div>}
  </div>;
  return ReactDOM.createPortal(<>{handoffGuard}{frost}{surface}</>, host);
}

function chip() { return { border: `1px solid ${ink(.1)}`, borderRadius: 9, padding: '6px 9px', background: ink(.045), color: ink(.62), font: 'inherit', fontSize: 11.5, cursor: 'pointer' }; }
/* Recents and Favorites fold on the app's ONE section bar now (2026-08-24).
   This file used to draw its own — a stroked, radius-11 bar with a sentence
   case label and a chevron typed as a character — beside an identical idea in
   the compose sheet's Details panel that was drawn properly. All that is left
   here is the one thing that is Tabs' own: the gap under the bar. */
function DisclosureBar({ icon, label, open, setOpen }) {
  return <window.MdSectionBar icon={icon} label={label} open={open}
    onToggle={() => setOpen((value) => !value)} style={{ marginBottom: 8 }} />;
}
function emptyText() { return { color: ink(.36), fontSize: 13, lineHeight: 1.5, padding: '12px 8px' }; }
Object.assign(window, { MdFinder, MdFindMark: FindMark, MdFindGlyph: FindGlyph,
  mdFindTargetRow: titleFromTarget, mdTabsHasReturn: mdTabsReturnCurrent, MD_FIND_KEY });
